const API_BASE_PROD = 'https://back-cardano-api.cardano-hwrimoveis.workers.dev';
const API_BASE_ENV = (process.env.REACT_APP_API_URL || '').trim();
const TOKEN_KEY = 'auth_token';
const CONTA_KEY = 'auth_conta';

const API_BASE = (() => {
  const base = API_BASE_ENV || API_BASE_PROD;
  const emPaginaHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

  const contemPlaceholder = /seudominio/i.test(base);

  let urlValida = true;
  try {
    // Valida formato de URL em tempo de execução sem quebrar build.
    // eslint-disable-next-line no-new
    new URL(base);
  } catch (_) {
    urlValida = false;
  }

  if (!urlValida || contemPlaceholder) {
    return API_BASE_PROD;
  }

  // Evita mixed content em produção quando a variável aponta para http://.
  if (emPaginaHttps && base.startsWith('http://')) {
    return API_BASE_PROD;
  }

  return base;
})();

async function requisitar(url, opcoes) {
  try {
    return await fetch(url, opcoes);
  } catch (_) {
    throw new Error(`Falha de rede ao conectar com a API (${API_BASE}). Verifique URL da API e CORS no backend.`);
  }
}

async function tratarResposta(res) {
  if (!res.ok) {
    const erro = await res.json().catch(() => ({ erro: 'Erro desconhecido' }));
    const mensagem = erro.erro || erro.message || `Erro ${res.status}`;

    if (mensagem.includes('Imported HMAC key length')) {
      throw new Error('Backend sem AUTH_JWT_SECRET configurado. Defina o secret no Cloudflare Worker e faça deploy da API.');
    }

    throw new Error(mensagem);
  }
  return res.json();
}

function montarHeadersJsonComAuth(tokenOpcional) {
  const token = tokenOpcional || obterToken();
  const headers = { 'Content-Type': 'application/json' };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function montarHeadersAuth(tokenOpcional) {
  const token = tokenOpcional || obterToken();
  const headers = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

export async function verificarSaude() {
  const res = await requisitar(`${API_BASE}/api/saude`);
  return tratarResposta(res);
}

export async function listarImoveis(pagina = 1, limite = 20) {
  const res = await requisitar(`${API_BASE}/api/imoveis?pagina=${pagina}&limite=${limite}`);
  return tratarResposta(res);
}

export async function buscarImoveis(filtros = {}) {
  const params = new URLSearchParams();

  if (filtros.q) params.set('q', String(filtros.q));
  if (filtros.tipo_operacao) params.set('tipo_operacao', String(filtros.tipo_operacao));
  if (filtros.tipo_imovel) params.set('tipo_imovel', String(filtros.tipo_imovel));

  if (Number.isFinite(filtros.min_valor)) {
    params.set('min_valor', String(filtros.min_valor));
  }

  if (Number.isFinite(filtros.max_valor)) {
    params.set('max_valor', String(filtros.max_valor));
  }

  params.set('pagina', String(filtros.pagina ?? 1));
  params.set('limite', String(filtros.limite ?? 20));

  const res = await requisitar(`${API_BASE}/api/imoveis?${params.toString()}`);
  return tratarResposta(res);
}

export async function buscarImovel(id) {
  const res = await requisitar(`${API_BASE}/api/imoveis/${id}`);
  return tratarResposta(res);
}

export async function criarImovel(payload) {
  const res = await requisitar(`${API_BASE}/api/imoveis`, {
    method: 'POST',
    headers: montarHeadersJsonComAuth(),
    body: JSON.stringify(payload),
  });
  return tratarResposta(res);
}

export async function atualizarImovel(id, payload) {
  const res = await requisitar(`${API_BASE}/api/imoveis/${id}`, {
    method: 'PUT',
    headers: montarHeadersJsonComAuth(),
    body: JSON.stringify(payload),
  });
  return tratarResposta(res);
}

export async function removerImovel(id) {
  const res = await requisitar(`${API_BASE}/api/imoveis/${id}`, {
    method: 'DELETE',
    headers: montarHeadersAuth(),
  });
  return tratarResposta(res);
}

export async function uploadImagem(arquivo, pasta = 'imoveis') {
  const form = new FormData();
  form.append('arquivo', arquivo);
  form.append('pasta', pasta);

  const res = await requisitar(`${API_BASE}/api/imagens`, {
    method: 'POST',
    headers: montarHeadersAuth(),
    body: form,
  });
  return tratarResposta(res);
}

export function obterUrlImagem(chave) {
  return `${API_BASE}/api/imagens/${encodeURIComponent(chave)}`;
}

export async function removerImagem(chave) {
  const res = await requisitar(`${API_BASE}/api/imagens/${encodeURIComponent(chave)}`, {
    method: 'DELETE',
    headers: montarHeadersAuth(),
  });
  return tratarResposta(res);
}

export async function atualizarGaleriaImovel(id, imagensChave, imagemChave) {
  const res = await requisitar(`${API_BASE}/api/imoveis/${id}/imagens`, {
    method: 'PUT',
    headers: montarHeadersJsonComAuth(),
    body: JSON.stringify({ imagensChave, imagemChave }),
  });
  return tratarResposta(res);
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function login(email, senha) {
  const res = await requisitar(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, senha }),
  });
  return tratarResposta(res);
}

export async function cadastrarConta(email, senha) {
  const res = await requisitar(`${API_BASE}/api/auth/cadastro`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, senha }),
  });
  return tratarResposta(res);
}

export async function cadastrarContaCliente(email, senha) {
  const res = await requisitar(`${API_BASE}/api/auth/cadastro-cliente`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, senha }),
  });
  return tratarResposta(res);
}

export async function cadastrarAdmin(email, senha, tokenAdmin) {
  const res = await requisitar(`${API_BASE}/api/auth/cadastro-admin`, {
    method: 'POST',
    headers: montarHeadersJsonComAuth(tokenAdmin),
    body: JSON.stringify({ email, senha }),
  });
  return tratarResposta(res);
}

export async function buscarContaLogada(token) {
  const res = await requisitar(`${API_BASE}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return tratarResposta(res);
}

export async function alterarSenha(senhaAtual, senhaNova) {
  const res = await requisitar(`${API_BASE}/api/auth/senha`, {
    method: 'PUT',
    headers: montarHeadersJsonComAuth(),
    body: JSON.stringify({ senhaAtual, senhaNova }),
  });
  return tratarResposta(res);
}

export async function recuperarSenha(email) {
  const res = await requisitar(`${API_BASE}/api/auth/recuperar-senha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return tratarResposta(res);
}

export async function redefinirSenhaComToken(token, senhaNova) {
  const res = await requisitar(`${API_BASE}/api/auth/redefinir-senha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, senhaNova }),
  });
  return tratarResposta(res);
}

export function salvarSessaoAutenticacao(token, conta) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (conta) localStorage.setItem(CONTA_KEY, JSON.stringify(conta));
}

export function salvarToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function salvarConta(conta) {
  localStorage.setItem(CONTA_KEY, JSON.stringify(conta));
}

export function obterToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function obterConta() {
  const raw = localStorage.getItem(CONTA_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function contaEhAdmin() {
  const conta = obterConta();
  return conta?.role === 'admin' && conta?.ativo !== false;
}

export function removerToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CONTA_KEY);
}

// ── Imóveis ───────────────────────────────────────────────────────────────────

export async function criarImovelComImagens(dadosImovel, arquivos) {
  const uploads = await Promise.all(arquivos.map((arquivo) => uploadImagem(arquivo)));
  const imagensChave = uploads.map((u) => u.chave);

  return criarImovel({
    ...dadosImovel,
    imagensChave,
    imagemChave: imagensChave[0] ?? null,
  });
}
